import { useEffect, useState } from 'react'

const AUTH_CHECK_LOCK_NAME = 'auth-token-check'

const isPageVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden'

const isAbortError = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError'

export const useAuthCheckLeader = (enabled: boolean) => {
  const supportsLocks =
    typeof navigator !== 'undefined' && Boolean(navigator.locks)
  const [pageVisible, setPageVisible] = useState(isPageVisible)
  const shouldCompeteForLeadership = enabled && pageVisible
  const [isLeader, setIsLeader] = useState(
    shouldCompeteForLeadership && !supportsLocks
  )

  useEffect(() => {
    if (!enabled) return

    const handleVisibilityChange = () => setPageVisible(isPageVisible())

    handleVisibilityChange()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [enabled])

  useEffect(() => {
    if (!shouldCompeteForLeadership) {
      setIsLeader(false)
      return
    }

    if (typeof navigator === 'undefined' || !navigator.locks) {
      // Authentication must continue to work in browsers without the Locks
      // API. Visible tabs retain the previous per-tab polling behaviour.
      setIsLeader(true)
      return
    }

    let disposed = false
    let releaseLock: (() => void) | undefined
    const abortController = new AbortController()

    setIsLeader(false)
    void navigator.locks
      .request(
        AUTH_CHECK_LOCK_NAME,
        { signal: abortController.signal },
        async () => {
          if (disposed) return

          setIsLeader(true)
          await new Promise<void>(resolve => {
            releaseLock = resolve
          })
        }
      )
      .catch(error => {
        if (disposed || isAbortError(error)) return

        // A broken Locks implementation must not disable authentication.
        setIsLeader(true)
      })

    return () => {
      disposed = true
      abortController.abort()
      releaseLock?.()
    }
  }, [shouldCompeteForLeadership])

  return isLeader
}
